import React, { useState } from 'react';
import { Tabs } from '@shared/ui/tabs';
import { MessageSquare, Clock, Send, User } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { Textarea } from '@shared/ui/textarea';
import { useParams } from 'react-router-dom';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useNotes, useAddNote, useActivityLog } from '../../../shared/hook';
import { cn } from '@shared/lib/utils';
import { useTranslation } from 'react-i18next';

const NotesComponent = ({ requestId }: { requestId?: string }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const { remoteUtilityId, firstName, lastName } = getLoginDataFromStorage();
  const fullName = `${firstName || ''}${lastName || ''}`.trim();


  const { data: notesData, refetch: refetchNotes } = useNotes({
    remote_utility_id: remoteUtilityId,
    request_id: requestId,
    source: "consumer_web"
  });

  const addNoteMutation = useAddNote();

  const handleAddNote = async () => {
    if (note.trim() && requestId && remoteUtilityId) {
      const payload = {
        source: 1,
        request_id: requestId,
        remote_utility_id: remoteUtilityId,
        notes: { note: note.trim() }
      };
      
      try {
        await addNoteMutation.mutateAsync(payload);
        setNote('');
        await refetchNotes();
      } catch (error) {
        console.error('Error adding note:', error);
      }
    }
  };

  const notesList = notesData?.result?.filter((item: any) => item.isActive) || [];

  return (
    <div className="space-y-6 mt-4">
      <h4 className="text-sm font-bold flex items-center gap-2 text-slate-700">
        <MessageSquare size={16} /> {t("Service Communication")}
      </h4>
      
      <div className="space-y-4">
        {notesList.length > 0 ? (
          notesList.map((item: any, index: number) => (
            <div key={index} className="bg-gray-50/80 border-l-4 border-gray-300 p-4 rounded-r-xl">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <User size={14} className="text-gray-400" />
                  <div className="font-semibold text-sm text-gray-900">{item.createdBy === fullName ? t("You") : (item.createdBy || t("Unknown"))}</div>
                </div>
                <div className="text-xs text-gray-400">
                  {item.createdDate ? new Date(item.createdDate).toLocaleString() : ''}
                </div>
              </div>
              <p className="text-sm text-gray-700 ml-5">{item.note}</p>
            </div>
          ))
        ) : (
          <p className="text-sm text-slate-500">{t("No communication history yet.")}</p>
        )}
      </div>

      <div className="pt-4 space-y-3">
        <h4 className="text-sm font-bold flex items-center gap-2 text-slate-700">
          <Send size={16} className="-rotate-45" /> {t("Add a Note")}
        </h4>
        <Textarea 
          placeholder={t("Enter your note or question here...")}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="min-h-[100px] resize-none border-gray-200 focus-visible:ring-blue-500 rounded-xl"
        />
        <Button 
          onClick={handleAddNote}
          disabled={!note.trim() || addNoteMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 flex items-center gap-2 font-medium"
        >
          <Send size={14} /> {t("Add a Note")}
        </Button>
      </div>
    </div>
  );
};

const TimelineComponent = ({ requestId }: { requestId?: string }) => {
  const { t } = useTranslation();
  const { remoteUtilityId } = getLoginDataFromStorage();

  const { data: activitylog } = useActivityLog({
    remote_utility_id: remoteUtilityId,
    id: requestId as string,
    module: "cx"
  });

  const events = activitylog?.results?.map((item: any) => ({
    title: item.title || t("Activity"),
    time: item.date && item.time 
      ? `${item.date} ${item.time}` 
      : item.date || t("Unknown Time"),
    desc: item.description || t("No description available.")
  })) || [];

  return (
    <div className="mt-6 px-4">
      {events.length > 0 ? (
        <div className="relative border-l border-gray-200 ml-3 space-y-8">
          {events.map((ev: any, i: number) => (
            <div key={i} className="relative pl-6">
              <div className="absolute w-3.5 h-3.5 bg-blue-500 border-2 border-blue-100 rounded-full -left-[7px] top-1.5" />
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-slate-800 text-sm">{ev.title}</span>
                <span className="text-xs text-gray-400">{ev.time}</span>
              </div>
              <p className="text-sm text-slate-500">{ev.desc}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">{t("No activity history available.")}</p>
      )}
    </div>
  );
};

export const SharedNotesTimeline = ({ data }: { data?: any }) => {
  const { t } = useTranslation();
  const requestId = data?.id || data?.request_id;
  
  const tabConfigs = {
    notes: {
      label: t("Notes"),
      icon: <MessageSquare size={16} />,
      component: <NotesComponent requestId={requestId} />
    },
    timeline: {
      label: t("Timeline"),
      icon: <Clock size={16} />,
      component: <TimelineComponent requestId={requestId} />
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4">
      <Tabs 
        tabComponents={tabConfigs} 
        defaultValue="notes" 
        disableUrlSync={true}
        tabsListFullWidth={true}
        tabsListClassName="grid grid-cols-2"
      />
    </div>
  );
};
